/**
 * Shared IndexedDB key/value store used for all bulky/media-ish persistence
 * (Dalle outputs, poster canvas JSON, video metadata, voices, GPT history,
 * musicgen URLs, etc.).
 *
 * - Single DB / single object store -> all keys live together.
 * - Memoized IDBDatabase handle so we only pay open() cost once per page.
 * - Per-key write coalescer: rapid setState bursts collapse to one transaction.
 * - Tiny boot-time flags (password, active tab, node id, ...) intentionally
 *   stay in localStorage so first paint can remain synchronous.
 */

const DB_NAME = "MusicGenAppDB";
const STORE_NAME = "kv";
const DB_VERSION = 1;

const COALESCE_DELAY_MS = 250;

let dbPromise = null;

function isIdbAvailable() {
  return typeof indexedDB !== "undefined" && indexedDB !== null;
}

function getDB() {
  if (!isIdbAvailable()) {
    return Promise.reject(new Error("IndexedDB is not available"));
  }
  if (dbPromise) return dbPromise;
  dbPromise = new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onerror = () => {
      dbPromise = null;
      reject(req.error);
    };
    req.onsuccess = () => {
      const db = req.result;
      db.onversionchange = () => {
        try { db.close(); } catch (_) {}
        dbPromise = null;
      };
      resolve(db);
    };
    req.onupgradeneeded = (e) => {
      const db = e.target.result;
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        db.createObjectStore(STORE_NAME);
      }
    };
  });
  return dbPromise;
}

function withStore(mode, fn) {
  return getDB().then((db) =>
    new Promise((resolve, reject) => {
      const tx = db.transaction(STORE_NAME, mode);
      const store = tx.objectStore(STORE_NAME);
      let result;
      tx.oncomplete = () => resolve(result);
      tx.onabort = () => reject(tx.error);
      tx.onerror = () => reject(tx.error);
      Promise.resolve(fn(store)).then((r) => { result = r; }, reject);
    })
  );
}

export function idbGet(key) {
  return withStore("readonly", (store) =>
    new Promise((resolve, reject) => {
      const req = store.get(key);
      req.onerror = () => reject(req.error);
      req.onsuccess = () => resolve(req.result === undefined ? null : req.result);
    })
  );
}

/**
 * Coalesced write. Multiple calls to idbSet for the same key within
 * COALESCE_DELAY_MS only write the latest value once. Returns a promise
 * that resolves when that latest value is committed (or when the entry is
 * superseded by a remove, in which case the value the caller asked to store
 * is gone but the user-visible "I requested this state" intent is still
 * satisfied).
 */
const pending = new Map(); // key -> { value, timer, resolvers: [], rejecters: [] }

function flushPending(key) {
  const entry = pending.get(key);
  if (!entry) return Promise.resolve();
  pending.delete(key);
  if (entry.timer) clearTimeout(entry.timer);
  return withStore("readwrite", (store) => {
    store.put(entry.value, key);
  })
    .then(() => entry.resolvers.forEach((r) => r()))
    .catch((err) => entry.rejecters.forEach((r) => r(err)));
}

export function idbSet(key, value) {
  return new Promise((resolve, reject) => {
    let entry = pending.get(key);
    if (!entry) {
      entry = { value, timer: null, resolvers: [], rejecters: [] };
      pending.set(key, entry);
    } else {
      entry.value = value;
      if (entry.timer) clearTimeout(entry.timer);
    }
    entry.resolvers.push(resolve);
    entry.rejecters.push(reject);
    entry.timer = setTimeout(() => flushPending(key), COALESCE_DELAY_MS);
  });
}

/**
 * Bypass the coalescer and write immediately. Use on visibilitychange:hidden
 * / pagehide where we want to fire a write right now (browsers usually let
 * in-flight IDB transactions complete after the document is hidden).
 *
 * The returned promise resolves only after the write actually commits.
 */
export function idbSetImmediate(key, value) {
  const entry = pending.get(key);
  if (entry) {
    entry.value = value;
    return new Promise((resolve, reject) => {
      entry.resolvers.push(resolve);
      entry.rejecters.push(reject);
      flushPending(key);
    });
  }
  return withStore("readwrite", (store) => {
    store.put(value, key);
  });
}

/**
 * Flush every coalesced pending write right now. Returns a promise that
 * resolves once all in-flight writes have committed (errors are swallowed
 * per-key so one failing write doesn't block the rest).
 */
export function flushAllPending() {
  const keys = Array.from(pending.keys());
  return Promise.all(
    keys.map((k) => Promise.resolve(flushPending(k)).catch(() => {}))
  );
}

export function idbRemove(key) {
  if (pending.has(key)) {
    const entry = pending.get(key);
    if (entry.timer) clearTimeout(entry.timer);
    pending.delete(key);
    // The caller's intent ("store this value") is being superseded by a
    // delete; resolve their promises rather than rejecting so save sites
    // don't surface a misleading "save failed" error.
    entry.resolvers.forEach((r) => r());
  }
  return withStore("readwrite", (store) => {
    store.delete(key);
  });
}

export function idbKeys() {
  return withStore("readonly", (store) =>
    new Promise((resolve, reject) => {
      const req = store.getAllKeys();
      req.onerror = () => reject(req.error);
      req.onsuccess = () => resolve(req.result || []);
    })
  );
}

export function idbRemoveByPrefix(prefix) {
  return idbKeys().then((keys) => {
    const matching = keys.filter(
      (k) => typeof k === "string" && k.startsWith(prefix)
    );
    if (matching.length === 0) return;
    return withStore("readwrite", (store) => {
      matching.forEach((k) => store.delete(k));
    });
  });
}
